import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { api, imgSrc } from "@/lib/api";
import { useI18n } from "@/i18n";

const pad = (n) => String(n).padStart(2, "0");

const Countdown = ({ endsAt, skew, onEnd }) => {
  const { t } = useI18n();
  const left = () => Math.max(0, new Date(endsAt).getTime() - (Date.now() + skew));
  const [ms, setMs] = useState(left);
  useEffect(() => {
    const id = setInterval(() => { const v = left(); setMs(v); if (v === 0) onEnd(); }, 1000);
    return () => { clearInterval(id); };
  }, [endsAt, skew]); // eslint-disable-line react-hooks/exhaustive-deps
  const s = Math.floor(ms / 1000);
  const parts = [[Math.floor(s / 86400), t("p4.promo.days")], [Math.floor((s % 86400) / 3600), t("p4.promo.hours")], [Math.floor((s % 3600) / 60), t("p4.promo.minutes")], [s % 60, t("p4.promo.seconds")]];
  return (
    <div className="mt-4" data-testid="promo-countdown">
      <p className="text-xs font-bold uppercase tracking-widest text-[#FF7A6B]">{t("p4.promo.endsIn")}</p>
      <div className="mt-2 grid grid-cols-4 gap-2">
        {parts.map(([v, l], i) => (
          <div key={l} className="rounded-2xl bg-[#3B1272] py-2.5 text-center text-white" data-testid={`promo-countdown-${i}`}>
            <span className="block font-display text-2xl font-extrabold tabular-nums">{i === 0 ? v : pad(v)}</span>
            <span className="text-[10px] font-semibold uppercase tracking-wider text-white/70">{l}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const PromoPopup = () => {
  const { t } = useI18n();
  const [p, setP] = useState(null);
  const [skew, setSkew] = useState(0);
  useEffect(() => {
    api.get("/promos/active").then(({ data }) => {
      const promo = data.promo;
      if (data.server_now) setSkew(new Date(data.server_now).getTime() - Date.now());
      if (promo && sessionStorage.getItem("juppo_promo_seen") !== promo.promo_id) setP(promo);
    }).catch(() => {});
  }, []);
  if (!p) return null;
  const close = () => { sessionStorage.setItem("juppo_promo_seen", p.promo_id); setP(null); };
  const external = p.cta_url?.startsWith("https://");
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-[#1E1B2E]/60 p-4 backdrop-blur-sm" onClick={close} data-testid="promo-popup">
      <div className="fade-up relative w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={close} aria-label={t("p4.promo.close")} className="absolute right-3 top-3 z-10 rounded-full bg-white/90 p-1.5 shadow" data-testid="promo-popup-close"><X className="h-5 w-5" /></button>
        {p.image_url && <img src={imgSrc(p.image_url)} alt="" className="h-56 w-full object-cover" data-testid="promo-popup-image" />}
        <div className="p-7">
          <h2 className="text-2xl font-extrabold text-[#3B1272]" data-testid="promo-popup-title">{p.title}</h2>
          {p.text && <p className="mt-2 whitespace-pre-line text-[#4B4763]" data-testid="promo-popup-text">{p.text}</p>}
          {p.show_countdown !== false && <Countdown endsAt={p.ends_at} skew={skew} onEnd={() => setP(null)} />}
          {p.cta_label && p.cta_url && (external
            ? <a href={p.cta_url} target="_blank" rel="noopener noreferrer" onClick={close} className="btn-coral mt-5 w-full !py-3" data-testid="promo-popup-cta">{p.cta_label}</a>
            : <Link to={p.cta_url} onClick={close} className="btn-coral mt-5 w-full !py-3" data-testid="promo-popup-cta">{p.cta_label}</Link>)}
        </div>
      </div>
    </div>
  );
};
